import React, { useCallback, useEffect, useRef, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { OtpInput } from "./otp-input";

const DEFAULT_OTP_COUNT_DOWN = 120;

interface OtpVerificationProps {
  phoneNumber: string;
  onChangePhoneNumber: () => void;
  onResendOtp: (recaptchaToken: string) => void;
  onSubmit: (otp: string) => void;
}

export const OtpVerification: React.FunctionComponent<OtpVerificationProps> = ({
  phoneNumber,
  onChangePhoneNumber,
  onResendOtp,
  onSubmit,
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [otpChallenge, setOtpChallenge] = useState<{ [key: number]: string }>({
    0: "",
    1: "",
    2: "",
    3: "",
    4: "",
    5: "",
  });
  const [countDown, setCountDown] = useState<number>(DEFAULT_OTP_COUNT_DOWN);
  const [resendEnabled, setResendEnabled] = useState<boolean>(false);

  const otp0 = useRef<HTMLInputElement>(null);
  const otp1 = useRef<HTMLInputElement>(null);
  const otp2 = useRef<HTMLInputElement>(null);
  const otp3 = useRef<HTMLInputElement>(null);
  const otp4 = useRef<HTMLInputElement>(null);
  const otp5 = useRef<HTMLInputElement>(null);

  useEffect(() => {
    otp0?.current?.focus();
  }, [otp0]);

  useEffect(() => {
    if (!resendEnabled) {
      if (countDown <= 0) {
        setResendEnabled(true);
        setCountDown(DEFAULT_OTP_COUNT_DOWN);
      } else {
        const timeout = setTimeout(() => setCountDown(countDown - 1), 1000);
        return () => clearTimeout(timeout);
      }
    }
  }, [countDown, resendEnabled]);

  useEffect(() => {
    // Restrict access from phone query param
    // Will redirect to AuthStep.InputPhoneNumber
    if (!phoneNumber) onChangePhoneNumber();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOtpInput = (value: string, index: number) => {
    if (isNaN(Number(value)) || value?.length > 1) {
      return;
    }

    const newOtps = {
      ...otpChallenge,
      [index]: value,
    };
    setOtpChallenge(newOtps);

    const otpKeys = Object.keys(newOtps);
    if (otpKeys.length === 6 && otpKeys.every((idx) => !!newOtps[idx])) {
      let otpData = "";
      otpKeys?.forEach((item) => (otpData += newOtps[item]));
      onSubmit(otpData);
    }

    if (!value) return;
    switch (index) {
      case 0:
        otp1.current?.focus();
        otp1.current?.select();
        break;
      case 1:
        otp2.current?.focus();
        otp2.current?.select();
        break;
      case 2:
        otp3.current?.focus();
        otp3.current?.select();
        break;
      case 3:
        otp4.current?.focus();
        otp4.current?.select();
        break;
      case 4:
        otp5.current?.focus();
        otp5.current?.select();
        break;
    }
  };

  const handleOnPaste = (e: React.ClipboardEvent) => {
    const pastedValue = e.clipboardData.getData("text");
    const newOtps = {};
    const data = pastedValue.split("");

    if (data.some((word) => isNaN(Number(word)))) {
      return;
    }

    data.forEach((word, index) => {
      newOtps[index] = word;
      switch (index - 1) {
        case 0:
          otp1.current?.focus();
          otp1.current?.select();
          break;
        case 1:
          otp2.current?.focus();
          otp2.current?.select();
          break;
        case 2:
          otp3.current?.focus();
          otp3.current?.select();
          break;
        case 3:
          otp4.current?.focus();
          otp4.current?.select();
          break;
        case 4:
          otp5.current?.focus();
          otp5.current?.select();
          break;
      }
    });
    setOtpChallenge(newOtps);

    const otpKeys = Object.keys(newOtps);
    if (otpKeys.length >= 6) {
      onSubmit(Object.values(newOtps).splice(0, 6).join(""));
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (otpChallenge[index]) return;
    if (e.key === "Backspace" || e.key === "Delete") {
      switch (index) {
        case 1:
          otp0.current?.focus();
          otp0.current?.select();
          break;
        case 2:
          otp1.current?.focus();
          otp1.current?.select();
          break;
        case 3:
          otp2.current?.focus();
          otp2.current?.select();
          break;
        case 4:
          otp3.current?.focus();
          otp3.current?.select();
          break;
        case 5:
          otp4.current?.focus();
          otp4.current?.select();
          break;
      }
    }
  };

  const handleResendOTP = useCallback(async () => {
    if (!executeRecaptcha) return;
    setResendEnabled(false);
    const recaptchaToken = await executeRecaptcha("request_otp_web_buyer");
    onResendOtp(recaptchaToken);
  }, [executeRecaptcha, onResendOtp]);

  return (
    <div className="bg-gray-100 flex flex-col px-4 h-screen justify-center">
      <h1 className="text-primary text-xl">Verifikasi Nomor</h1>
      <p className="my-4">
        Silahkan masukkan kode 6 digit yang telah kami kirimkan ke nomor{" "}
        <span className="font-bold">+62{phoneNumber}</span>
      </p>
      <div
        className="flex flex-row items-center my-16"
        id="auth-otp-input"
        data-testid="auth-otp-input"
      >
        <OtpInput
          ref={otp0}
          value={otpChallenge[0]}
          onChange={handleOtpInput}
          index={0}
          onPaste={handleOnPaste}
        />
        <OtpInput
          ref={otp1}
          value={otpChallenge[1]}
          onChange={handleOtpInput}
          index={1}
          onKeyDown={handleKeyDown}
        />
        <OtpInput
          ref={otp2}
          value={otpChallenge[2]}
          onChange={handleOtpInput}
          index={2}
          onKeyDown={handleKeyDown}
        />
        <OtpInput
          ref={otp3}
          value={otpChallenge[3]}
          onChange={handleOtpInput}
          index={3}
          onKeyDown={handleKeyDown}
        />
        <OtpInput
          ref={otp4}
          value={otpChallenge[4]}
          onChange={handleOtpInput}
          index={4}
          onKeyDown={handleKeyDown}
        />
        <OtpInput
          ref={otp5}
          value={otpChallenge[5]}
          onChange={handleOtpInput}
          index={5}
          onKeyDown={handleKeyDown}
        />
      </div>
      <p className="text-sm font-bold">Tidak menerima kode?</p>
      {resendEnabled ? (
        <p
          onClick={handleResendOTP}
          className="cursor-pointer text-sm py-3"
          id="auth-resend-otp-btn"
          data-testid="auth-resend-otp-btn"
        >
          KIRIM ULANG OTP
        </p>
      ) : (
        <p className="text-sm py-3">
          Kirim ulang kode setelah{" "}
          <span className="text-danger">{countDown} detik</span>
        </p>
      )}
      <p
        onClick={onChangePhoneNumber}
        className="text-primary text-sm cursor-pointer"
        id="auth-change-phone-btn"
        data-testid="auth-change-phone-btn"
      >
        UBAH NOMOR
      </p>
    </div>
  );
};
